import { useNavigate } from 'react-router-dom';
import { Target, Plus, ArrowRight, Calendar } from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';

interface Goal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  progress: number;
  daysLeft: number | null;
}

interface GoalsPreviewProps {
  goals: Goal[];
  isLoading?: boolean;
}

// Color variants based on progress
function getProgressColor(progress: number): { bg: string; fill: string; text: string } {
  if (progress >= 100) {
    return {
      bg: 'bg-green-100 dark:bg-green-900/20',
      fill: 'from-green-500 to-emerald-400',
      text: 'text-green-600 dark:text-green-400',
    };
  }
  if (progress >= 75) {
    return {
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      fill: 'from-blue-500 to-indigo-400',
      text: 'text-blue-600 dark:text-blue-400',
    };
  }
  if (progress >= 50) {
    return {
      bg: 'bg-amber-100 dark:bg-amber-900/20',
      fill: 'from-amber-500 to-yellow-400',
      text: 'text-amber-600 dark:text-amber-400',
    };
  }
  return {
    bg: 'bg-[#16c47f]/10 dark:bg-[#16c47f]/20',
    fill: 'from-[#16c47f] to-emerald-400',
    text: 'text-[#16c47f]',
  };
}

function GoalCard({ goal }: { goal: Goal }) {
  const navigate = useNavigate();
  const { formatAmount, isSecretMode } = useCurrency();
  const { t } = useTranslation();

  const colors = getProgressColor(goal.progress);
  const isCompleted = goal.progress >= 100;

  return (
    <div
      onClick={() => navigate('/goals')}
      className={`group relative p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
        isCompleted
          ? 'bg-gradient-to-r from-green-50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/5 border-green-200/50 dark:border-green-900/30 hover:shadow-lg hover:shadow-green-500/10'
          : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/50 hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-black/30 hover:border-gray-200 dark:hover:border-gray-600'
      }`}
    >
      {/* Completed Badge */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
          <svg
            className="w-3.5 h-3.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-0.5">
            {goal.name}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {isSecretMode ? '•••' : formatAmount(goal.currentAmount)} /{' '}
            {isSecretMode ? '•••' : formatAmount(goal.targetAmount)}
          </p>
        </div>
        <span className={`text-sm font-bold ${colors.text}`}>{Math.round(goal.progress)}%</span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full bg-gradient-to-r ${colors.fill} rounded-full transition-all duration-700`}
          style={{ width: `${Math.min(goal.progress, 100)}%` }}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
          {isSecretMode ? '•••' : formatAmount(goal.targetAmount - goal.currentAmount)}{' '}
          {t('dashboard.goals.remaining')}
        </span>
        {goal.daysLeft !== null && (
          <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
            <Calendar className="w-3 h-3" />
            {goal.daysLeft > 0 ? `${goal.daysLeft}j` : t('dashboard.goals.deadlinePassed')}
          </div>
        )}
      </div>
    </div>
  );
}

export function GoalsPreview({ goals, isLoading = false }: GoalsPreviewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12" />
        </div>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="p-4 rounded-xl border border-gray-100 dark:border-gray-700/50">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                </div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10" />
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gray-400" />
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">
            {t('dashboard.goals.title')}
          </p>
        </div>
        <button
          onClick={() => navigate('/goals')}
          className="text-xs text-[#16c47f] hover:text-[#12b070] font-medium flex items-center gap-1"
        >
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-gradient-to-br from-[#16c47f]/10 to-emerald-100/50 dark:from-[#16c47f]/20 dark:to-emerald-900/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Target className="w-7 h-7 text-[#16c47f]" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {t('dashboard.goals.empty')}
          </p>
          <Button
            onClick={() => navigate('/goals')}
            variant="outline"
            size="sm"
            className="border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('dashboard.goals.add')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.slice(0, 3).map((goal) => (
            <GoalCard key={goal.id} goal={goal} />
          ))}

          {goals.length > 3 && (
            <button
              onClick={() => navigate('/goals')}
              className="w-full py-2.5 text-xs font-medium text-[#16c47f] hover:text-[#12b070] flex items-center justify-center gap-1.5 bg-[#16c47f]/5 hover:bg-[#16c47f]/10 rounded-lg transition-colors"
            >
              {t('dashboard.goals.viewAll')} ({goals.length})
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
